import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Option } from '../models/option.model';
import { StyleManagerService } from './style-manager.service';
import { Observable, BehaviorSubject } from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  public smallLayout = false;
  private currentThemeSubject = new BehaviorSubject<string>(localStorage.getItem('theme') || 'slate-indigo');
  public currentTheme$ = this.currentThemeSubject.asObservable();

  public get currentThemeValue(): string {
    return this.currentThemeSubject.value;
  }

  constructor(
    private http: HttpClient,
    private styleManager: StyleManagerService,
    private responsive: BreakpointObserver
  ) {
    this.responsive.observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large])
      .subscribe(result => {
        this.smallLayout = result.matches;
      });
  }

  public getThemeOptions(): Observable<Array<Option>> {
    return this.http.get<Array<Option>>('assets/theme/options.json');
  }

  public isDarkTheme(themeName: string): boolean {
    return themeName === 'cyan-orange' || themeName === 'magenta-violet' || themeName === 'midnight-obsidian';
  }

  /**
   * Apply the selected theme and sync document surfaces for Safari iOS.
   * @param themeToSet Theme identifier
   */
  public setTheme(themeToSet: string): void {
    void this.applyTheme(themeToSet);
  }

  /**
   * Load theme stylesheet and synchronize root backgrounds.
   * @param themeToSet Theme identifier
   */
  private async applyTheme(themeToSet: string): Promise<void> {
    const isDark = this.isDarkTheme(themeToSet);
    document.documentElement.classList.toggle('dark', isDark);
    document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';

    await this.styleManager.setStyle('theme', `assets/theme/${themeToSet}.css`);

    this.syncDocumentSurfaces(isDark);
    this.currentThemeSubject.next(themeToSet);
  }

  /**
   * Force html/body backgrounds to match the active theme tokens.
   * Safari iOS can keep the previous paint when only the stylesheet href changes.
   * @param isDark Whether the active theme is dark
   */
  private syncDocumentSurfaces(isDark: boolean): void {
    const root = document.documentElement;
    const background = getComputedStyle(root).getPropertyValue('--mat-sys-background').trim();
    const fallbackBackground = isDark ? '#090d16' : '#f8fafc';
    const resolvedBackground = background || fallbackBackground;

    root.style.setProperty('background-color', resolvedBackground);
    document.body.style.setProperty('background-color', resolvedBackground);

    const appRoot = document.querySelector('app-root');
    if (appRoot instanceof HTMLElement) {
      appRoot.style.setProperty('background-color', resolvedBackground);
    }

    document.querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', resolvedBackground);
    document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]')
      ?.setAttribute('content', isDark ? 'black-translucent' : 'default');

    void root.offsetHeight;
  }
}
